import React, { useState } from 'react';
import { bookingInfo, heroData } from '../data/mock';
import { MessageCircle, CheckCircle, ExternalLink, Copy, Check } from 'lucide-react';

const BookingSection = () => {
  const [copied, setCopied] = useState(false);
  const whatsappLink = `https://wa.me/${heroData.whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(heroData.whatsappMessage)}`;

  const copyUpiId = async () => {
    try {
      await navigator.clipboard.writeText(bookingInfo.upiId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Google Docs viewer URL for PDF
  const googleDocsViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(bookingInfo.qrCodeImage)}&embedded=true`;

  return (
    <section id="booking" className="py-24 lg:py-32 bg-[#F8F7F2]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-20">
          <p className="text-sm tracking-[0.2em] text-[#6B8F71] mb-4 uppercase">
            {bookingInfo.subtitle}
          </p>
          <h2 className="text-3xl lg:text-4xl font-light text-[#333333]">
            {bookingInfo.title}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Steps */}
          <div className="bg-[#FFFEF8] p-10 lg:p-12">
            <h3 className="text-xl font-light text-[#333333] mb-8">
              Booking Steps
            </h3>
            
            <div className="space-y-6">
              {bookingInfo.steps.map((step, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-[#6B8F71]/10 text-[#6B8F71] text-sm font-medium">
                    {index + 1}
                  </div>
                  <p className="text-base text-[#666666] leading-relaxed pt-1">
                    {step}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-3 px-8 py-4 border border-[#333333] text-[#333333] text-sm font-medium tracking-wide hover:bg-[#333333] hover:text-[#FFFEF8] transition-all duration-300 w-full justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              Book via WhatsApp
            </a>
          </div>

          {/* Payment Info */}
          <div className="bg-[#FFFEF8] p-10 lg:p-12">
            <h3 className="text-xl font-light text-[#333333] mb-8">
              Payment Information
            </h3>

            {/* Price Display */}
            <div className="mb-8 p-6 bg-[#F8F7F2] text-center">
              <p className="text-sm text-[#666666] mb-2">Scanning Fee</p>
              <p className="text-4xl font-light text-[#333333]">{bookingInfo.price}</p>
            </div>

            {/* QR Code - Using Google Docs viewer */}
            <div className="mb-4 bg-white border border-[#E5E4DE] overflow-hidden">
              <iframe
                src={googleDocsViewerUrl}
                title="Payment QR Code - Canara Bank"
                className="w-full h-[350px]"
                style={{ border: 'none' }}
              />
            </div>

            {/* View QR Code link as fallback */}
            <a
              href={bookingInfo.qrCodeImage}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-6 flex items-center justify-center gap-2 text-sm text-[#6B8F71] hover:text-[#333333] transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Open QR Code in New Tab
            </a>

            {/* UPI ID */}
            <div className="mb-6 p-4 bg-[#F8F7F2]">
              <p className="text-xs text-[#999999] mb-2 text-center">UPI ID (click to copy)</p>
              <button
                onClick={copyUpiId}
                className="w-full flex items-center justify-center gap-2 p-3 bg-white border border-[#E5E4DE] hover:border-[#6B8F71] transition-colors group"
              >
                <span className="text-sm text-[#333333] font-mono">{bookingInfo.upiId}</span>
                {copied ? (
                  <Check className="w-4 h-4 text-[#6B8F71]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#BCBBB4] group-hover:text-[#6B8F71]" />
                )}
              </button>
              {copied && (
                <p className="text-xs text-[#6B8F71] text-center mt-2">Copied to clipboard!</p>
              )}
            </div>

            {/* Note */}
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#6B8F71] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#666666] leading-relaxed">
                {bookingInfo.note}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
